/**
 * Notification Service
 *
 * REST service for the notifications inbox. The live notification stream is
 * delivered over WebSocket (Laravel Echo) into the notification store; this
 * service is the durable, paginated history that backs the full
 * `/notifications` page.
 *
 * Extends CrudService for the paginated read (inherited `getPaginated`) and
 * delete, and uses direct apiClient calls for the status-only read/unread
 * mutations (mirroring FriendService.respondToFriendRequest).
 *
 * Backend contract (Api\NotificationController + MarkAllNotificationReadController):
 * - GET    /api/notification               paginated resource collection
 *          ({ data, links, meta }); supports `?page` and `?unread_only`;
 *          the page size is fixed at 20 (no `per_page`)
 * - PATCH  /api/notification/{id}           update read state
 *          body `{ read: true }`  → read_at = now()
 *          body `{ read: false }` → read_at = null
 * - DELETE /api/notification/{id}           delete one                  → 204
 * - DELETE /api/notifications/mark-all-read mark every unread read      → 204
 *   (note: plural "notifications", and the verb is DELETE)
 */

import { apiClient } from './api/client';
import { rethrowTyped } from './api/errors';
import { CrudService } from './crud.service';
import * as NotificationUtils from '../utils/notification.utils';

import type { ID } from '../types/common/id.types';
import type {
  NotificationItem,
  NotificationRespondAction,
  NotificationResponseOption,
} from '../types/notification.types';

// Relative to the client's base URL, which already ends in `/api` — like
// every other service path. (This one used to carry its own `/api` and so
// asked for `/api/api/notifications/mark-all-read`.)
const MARK_ALL_READ_URL = '/notifications/mark-all-read';

// =============================================================================
// NOTIFICATION SERVICE CLASS
// =============================================================================

export class NotificationService extends CrudService<NotificationItem> {
  constructor() {
    super({
      baseUrl: '/notification',
      // Not cached: every read asks the server. Notifications arrive live and
      // change on other devices, and a cached page was served as the inbox's
      // answer after an arrival. "1 new notification — Refresh" came back with
      // nothing new, and reopening the inbox showed the list without it; both
      // were reproduced on web and mobile.
      enableCaching: false,
    });
  }

  // ---------------------------------------------------------------------------
  // WRITE OPERATIONS (status-only; no response body relied upon)
  // ---------------------------------------------------------------------------

  /**
   * Mark a single notification as read.
   *
   * PATCH /api/notification/{id}  body { read: true }
   */
  public async markAsRead(id: ID): Promise<void> {
    await this.setReadState(id, true);
  }

  /**
   * Mark a single notification as unread.
   *
   * PATCH /api/notification/{id}  body { read: false }
   */
  public async markAsUnread(id: ID): Promise<void> {
    await this.setReadState(id, false);
  }

  private async setReadState(id: ID, read: boolean): Promise<void> {
    if (!id) {
      throw new Error('Notification ID is required to update read state.');
    }

    try {
      const response = await apiClient.patch(`${this.baseUrl}/${id}`, { read });
      if (response.status !== 200 && response.status !== 204) {
        throw new Error(`Failed to update notification ${id}`);
      }
    } catch (error) {
      console.error(`Failed to update notification ${id}:`, error);
      rethrowTyped(error, 'Unable to update notification. Please try again.');
    }
  }

  /**
   * Mark all of the authenticated user's notifications as read.
   *
   * DELETE /api/notifications/mark-all-read
   */
  public async markAllAsRead(): Promise<void> {
    try {
      const response = await apiClient.delete(MARK_ALL_READ_URL);
      if (response.status !== 200 && response.status !== 204) {
        throw new Error('Failed to mark all notifications as read');
      }
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
      rethrowTyped(
        error,
        'Unable to mark all notifications as read. Please try again.'
      );
    }
  }

  /**
   * Answer an invitation from its notification: send the chosen option's body
   * to the invitation's own route. The API wrote both, so nothing about the
   * kind is known here — a share, a joint session and a challenge invitation
   * are all answered by this one call.
   */
  public async respond(
    action: NotificationRespondAction,
    option: NotificationResponseOption
  ): Promise<void> {
    try {
      await apiClient.request({
        method: action.request.method,
        url: NotificationUtils.toClientPath(action.request.path),
        data: option.body,
      });
    } catch (error) {
      console.error('Failed to answer notification:', error);
      rethrowTyped(error, 'Unable to send your answer. Please try again.');
    }
  }

  /**
   * Delete a notification permanently.
   *
   * DELETE /api/notification/{id} (204)
   */
  public async remove(id: ID): Promise<void> {
    if (!id) {
      throw new Error('Notification ID is required to delete.');
    }

    try {
      await this.delete(id);
    } catch (error) {
      console.error(`Failed to delete notification ${id}:`, error);
      rethrowTyped(error, 'Unable to delete notification. Please try again.');
    }
  }
}

// =============================================================================
// SERVICE INSTANCE
// =============================================================================

/**
 * Default notification service instance.
 */
export const notificationService = new NotificationService();
